/**
 * Thunks for favorites server sync.
 *
 * Plain thunks (not createAsyncThunk) to stay consistent with the rest of
 * the codebase which uses the imperative AuthorizedApi class pattern.
 */

import { fetchFavorites, pushFavorites } from '../../api/favorites'
import { setFavoriteForEvent } from './index'
import { loadFavoriteIds } from './favoritesStorage'
import { isDefined } from '../../utils'

/**
 * Toggles a favorite for an event.
 *
 * 1. Dispatches setFavoriteForEvent (which writes to localStorage).
 * 2. If the user is logged in, fires PUT /api/v1/user/favorites with the
 *    complete updated list.
 *
 * @param {object}  config   - App config
 * @param {object}  auth     - Auth state
 * @param {number}  id       - Event ID
 * @param {boolean} favorite - true to add, false to remove
 * @param {number}  [year]   - Convention year (for localStorage key)
 */
export function toggleFavoriteWithSync (config, auth, id, favorite, year) {
  return (dispatch, getState) => {
    dispatch(setFavoriteForEvent({ id, favorite, year }))

    if (!isDefined(auth?.authToken)) {
      return
    }

    const updatedIds = getState().events?.favorite_ids ?? []
    pushFavorites(config, auth, updatedIds)
  }
}

/**
 * On app load, synchronizes favorites between localStorage and the server.
 *
 * For logged-in users:
 *  - If localStorage is empty: fetches from server and populates localStorage
 *    (restore-on-wipe).
 *  - If both local and server have non-empty, potentially differing lists:
 *    fetches from server; serverFavoritesLoaded unions them in the slice;
 *    then the merged list is pushed back to the server.
 *
 * Anonymous users: no-op.
 *
 * @param {object} config  - App config
 * @param {object} auth    - Auth state
 * @param {number} [year]  - Convention year (for localStorage key)
 */
export function syncFavoritesOnLoad (config, auth, year) {
  return (_dispatch, _getState) => {
    if (!isDefined(auth?.authToken)) {
      return
    }

    const storageYear = year ?? new Date().getFullYear()
    const localIds = loadFavoriteIds(storageYear)

    fetchFavorites(config, auth, storageYear, (mergedIds) => {
      // After the union merge: if the merged list is non-empty and the local
      // list was also non-empty, push back to server to close any gap.
      if (localIds.length > 0 && mergedIds.length > 0) {
        pushFavorites(config, auth, mergedIds)
      }
    })
  }
}
